/** The side of its anchor a popover opens on, when there is room for it there. */
export type PopoverPlacement = 'bottom' | 'left' | 'right' | 'top';

/**
 * The places a popover can take beside its anchor, each named for the side it takes and the anchor edge it lines up with: start is the left or top edge, end the right or bottom one.
 *
 * The margin keeps the 4px gap on the side that faces the anchor. The places below and above may also shrink the popover, see {@link SHRINKABLE}.
 */
const POSITION_OPTIONS = {
	'bottom-end': { area: 'span-left bottom', margin: '4px 0px 0px', shrinkable: true },
	'bottom-start': { area: 'span-right bottom', margin: '4px 0px 0px', shrinkable: true },
	'left-end': { area: 'left span-top', margin: '0px 4px 0px 0px', shrinkable: false },
	'left-start': { area: 'left span-bottom', margin: '0px 4px 0px 0px', shrinkable: false },
	'right-end': { area: 'right span-top', margin: '0px 0px 0px 4px', shrinkable: false },
	'right-start': { area: 'right span-bottom', margin: '0px 0px 0px 4px', shrinkable: false },
	'top-end': { area: 'span-left top', margin: '0px 0px 4px', shrinkable: true },
	'top-start': { area: 'span-right top', margin: '0px 0px 4px', shrinkable: true },
} as const;

type PositionOptionName = keyof typeof POSITION_OPTIONS;

/**
 * The sizing that lets a place below or above shrink the popover, when nothing has room for all of it.
 *
 * The popover may be as tall as the room on that side, less the gap. Its min-height is the smallest of three: the height of its content, the max-height it was given, and half the viewport less half the anchor.
 *
 * The first two are what the popover needs, so a place with room for that fits as it would without shrinking.
 *
 * The third is more than the room on the side with the least of it, so on that side a popover that needs more than the room cannot shrink to fit, and the browser tries the next place: the popover only ever shrinks on the side with the most room.
 */
const SHRINKABLE = 'max-height: min(var(--ac-popover-max-height, 100vh), calc(100% - 4px)); min-height: calc-size(max-content, min(size, var(--ac-popover-max-height, 100vh), 50vh - anchor-size(height) / 2 - 4px));';

/**
 * The places the browser tries, in order, once the start of the preferred side, which the popover is placed at by its own styles, has no room for all of it.
 *
 * Chromium tries no more than five, the least the spec allows, so each list keeps to five. Below and above are tried again as places that may shrink, which is why the start of the preferred side comes back first for those two.
 */
const FALLBACKS: Record<PopoverPlacement, readonly PositionOptionName[]> = {
	bottom: ['bottom-start', 'bottom-end', 'top-start', 'top-end'],
	left: ['left-end', 'right-start', 'right-end', 'bottom-start', 'top-start'],
	right: ['right-end', 'left-start', 'left-end', 'bottom-start', 'top-start'],
	top: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
};

let positionOptionsSheet: CSSStyleSheet | null = null;

/**
 * Places a popover on the preferred side of its anchor, and lets the browser move it to another side when there is no room there, or shrink it when there is room for all of it nowhere.
 *
 * The popover must already be anchored through position-anchor, and given its max-height through {@link setPopoverMaxHeight}. Its user agent "inset: 0" is left as it is, since that is what makes the position-area the space the popover is laid out in.
 *
 * The browser judges the room against the viewport, as the popover is in the top layer, and judges it again on every scroll and resize.
 */
export function placePopover(popover: HTMLElement, placement: PopoverPlacement): void {
	adoptPositionOptions();
	const option = POSITION_OPTIONS[`${placement}-start`];
	popover.style.positionArea = option.area;
	// this margin also replaces the user agent "margin: auto", which centers a popover in the viewport
	popover.style.margin = option.margin;
	popover.style.positionTryFallbacks = FALLBACKS[placement].map((name) => `--ac-popover-${name}`).join(', ');
}

/**
 * Sets the max-height of a popover, in pixels, or Infinity for none.
 *
 * It is also kept as a custom property, since a place that shrinks the popover sets a max-height of its own and must not let it grow past this one.
 */
export function setPopoverMaxHeight(popover: HTMLElement, maxHeight: number): void {
	if (Number.isFinite(maxHeight)) {
		popover.style.maxHeight = `${maxHeight}px`;
		popover.style.setProperty('--ac-popover-max-height', `${maxHeight}px`);
	} else {
		popover.style.maxHeight = '';
		popover.style.removeProperty('--ac-popover-max-height');
	}
}

/**
 * Adds the @position-try rules the fallbacks name to the document, the first time a popover is placed, or again if they were taken away.
 *
 * They must be rules in a style sheet, as an inline style cannot hold an at-rule. A popover in a shadow tree finds them too, since a name the shadow tree does not define is looked up in the tree of its host.
 */
function adoptPositionOptions(): void {
	if (!positionOptionsSheet) {
		positionOptionsSheet = new CSSStyleSheet();
		positionOptionsSheet.replaceSync(positionOptionsRules());
	}
	if (!document.adoptedStyleSheets.includes(positionOptionsSheet)) {
		document.adoptedStyleSheets = [...document.adoptedStyleSheets, positionOptionsSheet];
	}
}

function positionOptionsRules(): string {
	return Object.entries(POSITION_OPTIONS)
		.map(([name, { area, margin, shrinkable }]) => `@position-try --ac-popover-${name} { position-area: ${area}; margin: ${margin}; ${shrinkable ? SHRINKABLE : ''} }`)
		.join('\n');
}
